/**
 * Onde a tinta cai dentro de um módulo.
 *
 * Um módulo importado não é um bloco: é uma árvore de nós do componente
 * dinâmico, e cada nó pode declarar a classe do material que ele é — `calcmemat`
 * na biblioteca do cliente, `MDF`, `FITA`, `METAL`. Quem decide o que recebe a
 * tinta é essa declaração, e só ela. Nada aqui adivinha pelo nome da peça, pelo
 * tamanho ou pela posição: o componente já diz o que cada coisa é, e inventar
 * uma segunda regra ao lado dele daria respostas diferentes para os dois.
 *
 * As duas contas do arquivo:
 *
 * - `paintTargetPath` — soltando o material sobre uma malha, qual nó recebe a
 *   pintura. Sobe da malha apontada até o nó mais próximo que declara a própria
 *   classe. É esse nó que é a peça: a malha é só a geometria dentro dela, e a
 *   fita de borda é peça própria porque declara `FITA`.
 * - `paintedMaterialFor` — desenhando, qual material vale para uma malha. A
 *   pintura mais próxima ganha, e ela não atravessa ferragem: pintar a porta
 *   inteira não pinta o puxador.
 */

import type { DynamicComponentGraph, DynamicNode } from "@calcplan/dynamic-components";
import {
  FACTORY_SCHEMA,
  resolveClass,
  type ExtractionSchema,
  type MaterialKind,
} from "@calcplan/cutlist";

/** Separador de segmentos do caminho, o mesmo do grafo. */
const SEPARATOR = " / ";

/** Chave da pintura que vale para o módulo inteiro. */
export const WHOLE_ELEMENT_SLOT = "default";

/**
 * Classes que representam superfícies de acabamento no módulo.
 *
 * A regra é positiva porque SKPs antigos podem deixar trilhos, cestos e outras
 * ferragens sem classe. Aceitar tudo que não fosse explicitamente metal faria
 * esses componentes internos herdarem a textura do móvel.
 */
const PAINTABLE_KINDS: ReadonlySet<MaterialKind> = new Set<MaterialKind>([
  "panel",
  "solid_wood",
  "veneer",
  "edge_band",
]);

/** A classe que o nó declara, sem herdar de ninguém. Nulo quando não declara. */
export type DeclaredKindLookup = (path: string) => MaterialKind | null;

/**
 * A classe declarada em cada nó do grafo.
 *
 * O nome do atributo vem do dicionário da extração — `calcmemat` de fábrica —,
 * e o mapa de classes é o mesmo que a lista de corte usa para dizer que `FITA`
 * é fita de borda e `METAL` é barra. Ler daqui e não de uma tabela própria é o
 * que garante que a tinta e a lista de corte enxerguem a mesma peça.
 */
export function declaredKindsOf(
  graph: DynamicComponentGraph,
  schema: ExtractionSchema = FACTORY_SCHEMA,
): Map<string, MaterialKind> {
  const names = schema.dictionary.materialClass ?? [];
  const kinds = new Map<string, MaterialKind>();
  const walk = (node: DynamicNode): void => {
    for (const name of names) {
      const declared = node.attributes[name.toLocaleLowerCase("en-US")]?.value;
      if (typeof declared !== "string" || !declared.trim()) continue;
      const { kind } = resolveClass(schema, declared);
      if (kind) kinds.set(node.path, kind);
      break;
    }
    for (const child of node.children) walk(child);
  };
  walk(graph.root);
  return kinds;
}

/** Consulta pronta para as contas deste arquivo. */
export function lookupFrom(kinds: ReadonlyMap<string, MaterialKind>): DeclaredKindLookup {
  return (path) => kinds.get(path) ?? null;
}

/** O caminho e todos os seus ancestrais, do mais fundo para a raiz. */
export function selfAndAncestors(path: string): string[] {
  const segments = path.split(SEPARATOR);
  const out: string[] = [];
  for (let length = segments.length; length > 0; length -= 1) {
    out.push(segments.slice(0, length).join(SEPARATOR));
  }
  return out;
}

/** Se `path` é o próprio `ancestor` ou está dentro dele. */
export function isWithin(path: string, ancestor: string): boolean {
  return path === ancestor || path.startsWith(ancestor + SEPARATOR);
}

/**
 * A peça que recebe a tinta quando se solta sobre esta malha.
 *
 * A malha apontada costuma ser a geometria anônima dentro da peça — o nó que
 * carrega o `material` do arquivo, sem nome nem classe. Quem tem identidade de
 * peça é o pai que declara a classe, e é nele que a pintura fica: assim a peça
 * inteira muda, e não um pedaço dela. Ferragem, metal e geometria sem classe
 * não respondem à paleta de materiais.
 */
export function paintTargetPath(meshPath: string, declaredKind: DeclaredKindLookup): string | null {
  for (const candidate of selfAndAncestors(meshPath)) {
    const kind = declaredKind(candidate);
    if (kind !== null) return PAINTABLE_KINDS.has(kind) ? candidate : null;
  }
  return null;
}

export function isPaintableMaterialPath(
  meshPath: string,
  declaredKind: DeclaredKindLookup,
): boolean {
  const kind = nearestDeclaredMaterialKind(meshPath, declaredKind);
  return kind !== null && PAINTABLE_KINDS.has(kind);
}

function nearestDeclaredMaterialKind(
  meshPath: string,
  declaredKind: DeclaredKindLookup,
): MaterialKind | null {
  for (const candidate of selfAndAncestors(meshPath)) {
    const kind = declaredKind(candidate);
    if (kind !== null) return kind;
  }
  return null;
}

export function canPaintMaterialTarget(
  targetPath: string,
  declaredKinds: ReadonlyMap<string, MaterialKind>,
): boolean {
  if (isPaintableMaterialPath(targetPath, lookupFrom(declaredKinds))) return true;
  for (const [candidate, kind] of declaredKinds) {
    if (isWithin(candidate, targetPath) && PAINTABLE_KINDS.has(kind)) return true;
  }
  return false;
}

/**
 * O material pintado que vale para a malha, ou nulo para manter o do arquivo.
 *
 * A pintura mais próxima ganha: pintar o módulo inteiro e depois uma porta
 * deixa a porta com o segundo material, sem precisar desfazer o primeiro. A
 * pintura do módulo entra como se estivesse na raiz, que é o que ela é.
 */
export function paintedMaterialFor(
  assignments: Readonly<Record<string, { materialId?: string } | undefined>>,
  meshPath: string,
  declaredKind: DeclaredKindLookup,
  requireDeclaredSurface = true,
): string | null {
  const nearestKind = nearestDeclaredMaterialKind(meshPath, declaredKind);
  if (requireDeclaredSurface && nearestKind !== null && !PAINTABLE_KINDS.has(nearestKind)) return null;
  // Alguns SKPs legados só classificam parte da árvore. Uma pintura do módulo
  // inteiro precisa alcançar também as superfícies sem classificação; caso
  // contrário o estado paramétrico muda, mas o material baked continua no 3D.
  // A exceção é conservadora: uma superfície explicitamente classificada como
  // ferragem continua protegida, e uma pintura parcial não atravessa um grupo
  // desconhecido.
  if (requireDeclaredSurface && nearestKind === null) {
    return assignments[WHOLE_ELEMENT_SLOT]?.materialId ?? null;
  }
  const chain = selfAndAncestors(meshPath);
  for (const candidate of chain) {
    const painted = assignments[candidate]?.materialId;
    if (painted) return painted;
  }
  const whole = assignments[WHOLE_ELEMENT_SLOT]?.materialId;
  return whole || null;
}

/**
 * Grava uma pintura e remove atribuições mais específicas que ela sobrescreve.
 * Assim, pintar uma porta depois do móvel cria uma exceção, e pintar o móvel
 * novamente elimina essa exceção antiga.
 */
export function replacePaintAssignment<T>(
  assignments: Readonly<Record<string, T>>,
  targetPath: string | null,
  assignment: T,
): Record<string, T> {
  const slot = targetPath ?? WHOLE_ELEMENT_SLOT;
  const next: Record<string, T> = {};
  for (const [candidate, current] of Object.entries(assignments)) {
    const overwritten = targetPath === null || isWithin(candidate, targetPath);
    if (!overwritten) next[candidate] = current;
  }
  next[slot] = assignment;
  return next;
}

/**
 * Quais malhas uma pintura em `targetPath` alcança, entre as candidatas.
 *
 * É a mesma conta do desenho, usada antes de soltar: o que vai ficar pintado é
 * o que fica realçado, então o realce não promete nada que a soltura não
 * cumpra — inclusive o puxador que não vai mudar de cor.
 */
export function meshesReachedByPaint<T>(
  meshes: readonly T[],
  pathOf: (mesh: T) => string | null,
  targetPath: string,
  declaredKind: DeclaredKindLookup,
  requireDeclaredSurface = true,
): T[] {
  return meshes.filter((mesh) => {
    const path = pathOf(mesh);
    if (!path || !isWithin(path, targetPath)) return false;
    return !requireDeclaredSurface || isPaintableMaterialPath(path, declaredKind);
  });
}
